import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Search, Loader2, Mic, X } from 'lucide-react';
import { noAutofillProps } from '../../utils/noAutofill';

/**
 * Self-contained speaker typeahead for the speaker_meeting component form.
 * Links a parsed/edited speaker_meeting to a persistent Speaker record.
 *
 * Props:
 *  - speakerId: currently linked speaker id (string | null)
 *  - speakerName: display name to show when linked but not yet loaded
 *  - onPick(speaker): called with the selected speaker object (or null to clear)
 */
const SpeakerLinkField = ({ speakerId, speakerName, onPick }) => {
  const [linked, setLinked] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // Resolve the linked speaker's name for display
  useEffect(() => {
    let cancelled = false;
    if (speakerId && !linked) {
      (async () => {
        try {
          const apiBase = getApiBase();
          const headers = getAuthHeaders();
          const res = await axios.get(`${apiBase}/speakers/${speakerId}`, { headers });
          if (!cancelled) setLinked(res.data);
        } catch {
          if (!cancelled && speakerName) setLinked({ id: speakerId, full_name: speakerName });
        }
      })();
    }
    if (!speakerId) setLinked(null);
    return () => { cancelled = true; };
  }, [speakerId, speakerName, linked]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/speakers`, { headers, params: { q: query.trim(), limit: 8 } });
        setResults(res.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const name = (s) => s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim();

  if (speakerId && linked) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-ui-red-line bg-ui-red-bg px-3 py-2">
        <span className="flex items-center gap-2 text-sm font-medium text-ui-ink">
          <Mic className="h-4 w-4 text-ui-red-fg" />
          {name(linked) || speakerName || 'Linked speaker'}
          {linked.email ? <span className="text-ui-muted">{linked.email}</span> : null}
        </span>
        <button type="button" onClick={() => { onPick && onPick(null); setLinked(null); }} className="text-ui-muted hover:text-ui-red-fg">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-muted" />
        <input
          {...noAutofillProps}
          name="speaker-link-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Link to a saved speaker (optional)..."
          className="h-11 w-full rounded-lg border border-ui-line pl-9 pr-3 text-base"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ui-muted" />}
      </div>
      {results.length > 0 && (
        <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-ui-line">
          {results.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => { onPick && onPick(s); setLinked(s); setQuery(''); setResults([]); }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-ui-red-bg"
            >
              <span className="font-medium text-ui-ink">{name(s)}</span>
              {s.email && <span className="text-xs text-ui-muted">{s.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpeakerLinkField;
